/**
 * Firebase Auth Sync Service
 *
 * This service ensures Firebase authentication state stays in sync with the Rails session.
 *
 * Key responsibilities:
 * 1. Listen for Firebase auth state changes (including restoration from localStorage)
 * 2. Automatically sync with Rails when Firebase auth is restored on page load
 * 3. Handle token refresh to prevent expiration during long sessions
 * 4. Update window.user for legacy code compatibility
 *
 * This solves the issue where:
 * - User logs in via Firebase (auth stored in localStorage)
 * - User navigates to plan_trail or refreshes the page
 * - Firebase restores auth from localStorage, but Rails session may have expired
 * - This service detects the Firebase auth and re-syncs with Rails
 */

import { getAuth, onAuthStateChanged, signInWithCustomToken, User } from 'firebase/auth';
import { app } from './initialise';
import axiosClient from '../helpers/axios_client';
import { signalAuthReady } from './authReady';

// Track if we've already synced on this page load
let hasSyncedOnPageLoad = false;
// Whether Firebase has reported a signed-in user on this page load. A null
// before that is "no Firebase session in this browser", not a sign-out.
let sawFirebaseUser = false;
// The page-load restore (see restoreFirebaseSessionFromRails) runs at most
// once; a save or keep-alive may try again later.
let restoreAttemptedOnPageLoad = false;
let restoreInFlight: Promise<boolean> | null = null;
let authListenerUnsubscribe: (() => void) | null = null;

/**
 * Whether Rails rendered a signed-in user into this page (layouts/_user_json_window).
 */
export function railsHasUser(): boolean {
  return !!(window as any).user;
}

async function requestSessionRestore(): Promise<boolean> {
  const auth = getAuth(app);
  if (auth.currentUser) return true;
  if (!railsHasUser()) return false;

  try {
    const response = await axiosClient.post('/firebase/session_restore', {}, {
      headers: { Accept: 'application/json' },
    });
    const token = response?.data?.token;
    if (!token) {
      console.warn('[AuthSync] Rails did not return a custom token');
      return false;
    }
    await signInWithCustomToken(auth, token);
    console.log('[AuthSync] Firebase session restored from the Rails session');
    return true;
  } catch (error) {
    // 401: the Rails session is gone too (logged out in another tab); the
    // caller falls back to asking the user to sign in.
    console.warn('[AuthSync] Could not restore the Firebase session from Rails:', error);
    return false;
  }
}

/**
 * Rails has a signed-in user but the Firebase SDK has none: the browser
 * cleared Firebase's IndexedDB, or the refresh token was revoked. Rails only got
 * its session from a verified Firebase token for this uid, so it mints a custom
 * token (POST /firebase/session_restore) and the SDK signs in again silently.
 * Without this the planner could not save while every page said "signed in"
 * (2026-11-04). Resolves true when Firebase has a user afterwards. Concurrent
 * callers share one request.
 */
export function restoreFirebaseSessionFromRails(): Promise<boolean> {
  if (restoreInFlight) return restoreInFlight;

  const attempt = requestSessionRestore();
  restoreInFlight = attempt;
  attempt.finally(() => {
    if (restoreInFlight === attempt) restoreInFlight = null;
  });
  return attempt;
}

/**
 * Fetch the backend user object and set window.user
 */
async function fetchAndSetBackendUser(): Promise<boolean> {
  try {
    const response = await axiosClient.get('/user/current');
    if (response.data) {
      (window as any).user = response.data;
      console.log('[AuthSync] Backend user fetched and set to window.user');
      return true;
    }
    return false;
  } catch (error) {
    console.warn('[AuthSync] Failed to fetch backend user:', error);
    return false;
  }
}

/**
 * Sync Firebase auth with Rails session
 * Sends the Firebase ID token to the backend to establish/refresh the Rails session
 */
async function syncWithRails(user: User): Promise<boolean> {
  try {
    // Get a fresh token (force refresh to ensure it's valid)
    const token = await user.getIdToken(true);

    // Send to Rails to establish/refresh session
    const response = await fetch(`/firebase/auth.json?accessToken=${token}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (response.ok) {
      console.log('[AuthSync] Successfully synced Firebase auth with Rails session');
      // Fetch and set the backend user object
      await fetchAndSetBackendUser();
      return true;
    } else {
      console.warn('[AuthSync] Failed to sync with Rails:', response.status);
      return false;
    }
  } catch (error) {
    console.error('[AuthSync] Error syncing with Rails:', error);
    return false;
  }
}

/**
 * Handle Firebase auth state change
 */
export async function handleAuthStateChange(user: User | null): Promise<void> {
  if (user) {
    sawFirebaseUser = true;
    // If we haven't synced on this page load and Rails might not know about the user,
    // sync with Rails to ensure the session is valid
    if (!hasSyncedOnPageLoad) {
      hasSyncedOnPageLoad = true;

      // Check if Rails already has a session by looking for user data injected by Rails
      const railsAlreadyHasSession = !!(window as any).currentUserId || !!(window as any).userSignedIn;

      if (!railsAlreadyHasSession) {
        console.log('[AuthSync] Firebase user found but Rails session may be missing, syncing...');
        // syncWithRails will also fetch and set the backend user
        await syncWithRails(user);
      } else {
        console.log('[AuthSync] Firebase user found and Rails session exists');
        // Rails session exists, fetch the backend user if not already set
        if (!(window as any).user) {
          await fetchAndSetBackendUser();
        }
      }
    }
  } else if (sawFirebaseUser) {
    // A Firebase user we saw earlier on this page has signed out.
    (window as any).user = null;
    sawFirebaseUser = false;
    hasSyncedOnPageLoad = false;
    // No restore here: this is also what a logout in another tab looks like,
    // and a restore racing that tab's DELETE /logout would sign the user back in.
  } else if (railsHasUser() && !restoreAttemptedOnPageLoad) {
    // Firebase has no session in this browser but Rails does. Keep the user
    // Rails rendered into window.user (clearing it made the planner treat
    // signed-in users as signed out) and sign Firebase back in from the Rails
    // session. On success Firebase reports the user next, and Rails already
    // has the session, so that report needs no sync.
    restoreAttemptedOnPageLoad = true;
    const restored = await restoreFirebaseSessionFromRails();
    if (restored) {
      hasSyncedOnPageLoad = true;
    }
  }
  // Logging out clears window.user itself (utils/auth/logout.ts).
}

export function resetAuthSyncForTests(): void {
  hasSyncedOnPageLoad = false;
  sawFirebaseUser = false;
  restoreAttemptedOnPageLoad = false;
  restoreInFlight = null;
}

/**
 * Initialize the auth sync service
 * Call this early in your application bootstrap
 */
export function initAuthSync(): void {
  if (authListenerUnsubscribe) {
    console.log('[AuthSync] Already initialized');
    return;
  }

  const auth = getAuth(app);

  console.log('[AuthSync] Initializing Firebase auth sync service');

  // Set up the auth state listener
  // signalAuthReady AFTER handleAuthStateChange completes so that
  // window.user is set before anything awaiting authReady proceeds.
  authListenerUnsubscribe = onAuthStateChanged(auth, async (user) => {
    try {
      await handleAuthStateChange(user);
    } finally {
      signalAuthReady();
    }
  });
}

/**
 * Stop the auth sync service
 */
export function stopAuthSync(): void {
  if (authListenerUnsubscribe) {
    authListenerUnsubscribe();
    authListenerUnsubscribe = null;
    console.log('[AuthSync] Stopped Firebase auth sync service');
  }
}

/**
 * Force a sync with Rails (useful after long periods of inactivity). With no
 * Firebase user it first tries to restore one from the Rails session, so a
 * planner save in the drifted state goes through instead of prompting.
 */
export async function forceSync(): Promise<boolean> {
  const auth = getAuth(app);
  let user = auth.currentUser;

  if (!user && await restoreFirebaseSessionFromRails()) {
    user = auth.currentUser;
  }

  if (user) {
    return syncWithRails(user);
  }

  console.log('[AuthSync] No Firebase user to sync');
  return false;
}

/**
 * Check if Firebase has a current user
 */
export function hasFirebaseUser(): boolean {
  const auth = getAuth(app);
  return !!auth.currentUser;
}

/**
 * Get the current Firebase user
 */
export function getCurrentFirebaseUser(): User | null {
  const auth = getAuth(app);
  return auth.currentUser;
}
